/**
 * Product recipe → three.js. The ONLY place catalogue primitives become meshes:
 * the room scene, thumbnails and the catalogue render check
 * (scripts/catalog/render.ts) all call `buildProductObject`, so what an author
 * reviews is exactly what a user sees.
 *
 * Geometry is built in millimetres, merged per material, and the returned
 * group is scaled to metres. Results are cached per recipe; callers must not
 * dispose the shared geometries or materials.
 */
import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { RoundedBoxGeometry } from 'three/examples/jsm/geometries/RoundedBoxGeometry.js';
import type { Recipe, RecipeMaterial } from '@roomtreatment/av-catalog';
import { buildRecipe, type Primitive } from '@roomtreatment/product-geometry';

const FINISH: Record<RecipeMaterial['finish'], { roughness: number; metalness: number }> = {
  matte: { roughness: 0.9, metalness: 0 },
  satin: { roughness: 0.55, metalness: 0 },
  gloss: { roughness: 0.18, metalness: 0 },
  metal: { roughness: 0.35, metalness: 0.85 },
  wood: { roughness: 0.7, metalness: 0 },
};

const materials = new Map<string, THREE.MeshStandardMaterial>();
function materialFor(m: RecipeMaterial): THREE.MeshStandardMaterial {
  const key = `${m.color}/${m.finish}`;
  let mat = materials.get(key);
  if (!mat) {
    // Double-sided: lathe profiles (cones, domes, rings) are open surfaces seen from both sides.
    mat = new THREE.MeshStandardMaterial({ color: new THREE.Color(m.color), side: THREE.DoubleSide, ...FINISH[m.finish] });
    materials.set(key, mat);
  }
  return mat;
}

const LATHE_SEGMENTS = 32;

function roundedRectShape(w: number, d: number, r: number): THREE.Shape {
  const s = new THREE.Shape();
  const x = -w / 2;
  const y = -d / 2;
  s.moveTo(x + r, y);
  s.lineTo(x + w - r, y);
  s.quadraticCurveTo(x + w, y, x + w, y + r);
  s.lineTo(x + w, y + d - r);
  s.quadraticCurveTo(x + w, y + d, x + w - r, y + d);
  s.lineTo(x + r, y + d);
  s.quadraticCurveTo(x, y + d, x, y + d - r);
  s.lineTo(x, y + r);
  s.quadraticCurveTo(x, y, x + r, y);
  return s;
}

function geometryFor(p: Primitive): THREE.BufferGeometry {
  if (p.shape === 'prism') {
    const shape = new THREE.Shape(p.points.map(([x, y]) => new THREE.Vector2(x, y)));
    return new THREE.ExtrudeGeometry(shape, { depth: p.depth, bevelEnabled: false });
  }
  if (p.shape === 'lathe') {
    return new THREE.LatheGeometry(
      p.points.map(([r, h]) => new THREE.Vector2(Math.max(r, 0), h)),
      p.segments ?? LATHE_SEGMENTS,
      p.phiStart ?? 0,
    );
  }
  const [w, h, d] = p.size;
  if (p.sideRadius > 0) {
    // Plan-view rounded rectangle extruded along local y, bevelled by `radius`.
    const bevel = Math.min(p.radius, h / 2 - 0.01);
    const inset = Math.max(bevel, 0);
    const shape = roundedRectShape(w - 2 * inset, d - 2 * inset, Math.max(0.01, p.sideRadius - inset));
    const g = new THREE.ExtrudeGeometry(shape, {
      depth: h - 2 * inset,
      bevelEnabled: inset > 0,
      bevelThickness: inset,
      bevelSize: inset,
      bevelSegments: 2,
      curveSegments: 6,
    });
    g.translate(0, 0, -(h - 2 * inset) / 2);
    g.rotateX(-Math.PI / 2);
    return g;
  }
  if (p.radius > 0) return new RoundedBoxGeometry(w, h, d, 2, p.radius);
  return new THREE.BoxGeometry(w, h, d);
}

function placed(p: Primitive): THREE.BufferGeometry {
  const g = geometryFor(p);
  const m = new THREE.Matrix4().makeBasis(
    new THREE.Vector3(...p.basis.x),
    new THREE.Vector3(...p.basis.y),
    new THREE.Vector3(...p.basis.z),
  );
  m.setPosition(...p.at);
  g.applyMatrix4(m);
  // Merge needs identical attribute sets.
  const flat = g.index ? g.toNonIndexed() : g;
  if (flat !== g) g.dispose();
  for (const name of Object.keys(flat.attributes)) {
    if (name !== 'position' && name !== 'normal') flat.deleteAttribute(name);
  }
  return flat;
}

const cache = new Map<string, THREE.Group>();

/** A group in metres, origin at the bottom-centre of the product, front facing +Z. */
export function buildProductObject(recipe: Recipe): THREE.Group {
  const key = JSON.stringify(recipe);
  const hit = cache.get(key);
  if (hit) return hit.clone();

  const built = buildRecipe(recipe);
  const byMaterial = new Map<string, { mat: THREE.MeshStandardMaterial; parts: THREE.BufferGeometry[] }>();
  for (const prim of built.primitives) {
    const mat = materialFor(prim.material);
    const entry = byMaterial.get(mat.uuid) ?? { mat, parts: [] };
    entry.parts.push(placed(prim));
    byMaterial.set(mat.uuid, entry);
  }
  const group = new THREE.Group();
  for (const { mat, parts } of byMaterial.values()) {
    const merged = mergeGeometries(parts, false);
    for (const g of parts) g.dispose();
    if (!merged) continue;
    const mesh = new THREE.Mesh(merged, mat);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    group.add(mesh);
  }
  group.scale.setScalar(0.001);
  cache.set(key, group);
  return group.clone();
}

export function triangleCount(object: THREE.Object3D): number {
  let n = 0;
  object.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (mesh.isMesh) {
      const g = mesh.geometry;
      n += (g.index ? g.index.count : g.attributes.position.count) / 3;
    }
  });
  return n;
}
